import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';

import LabelWithTooltip from '../../../../../shared/components/add-time-entry/validation-info/LabelWithTooltip';
import {
	NoteRequiredError,
	NoteRequiredInfoIcon,
} from '../../../../../shared/components/add-time-entry/validation-info/NoteRequiredInfo';
import {convertIntoFloatHoursFormatWithGranularity} from '../../../../../shared/components/inputs/hours-input/hours_input_logic';
import {HoursInput} from '../../../../../shared/components/inputs/hours-input/hours_input_view';
import useUpdateEffect from '../../../../../shared/hooks/useUpdateEffect';
import {
	isBillableSplitAllowed,
	isRolesAllowed,
	isTimeRegistrationNoteRequired,
} from '../../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import Util from '../../../../../shared/util/util';
import {ContentWrapper, Label, Notes} from './AdvancedTimeRegistrationStyled';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {Role} from '../../../../../shared/components/modals/time-registration/time-registration-modal/types/TimeRegistrationRole';
import {RemainingTimeAndError} from '../../../../../shared/components/add-time-entry/validation-info/TimeExceedsEstimateInfo';
import ForecastTooltip from '../../../../../shared/components/tooltips/ForecastTooltip.js';
import RoleDropdownRelayWrapper from '../../../../../shared/components/dropdowns/role-dropdown/RoleDropdownRelayWrapper';
import {RoleDropdownLabelFormat} from '../../../../../shared/components/dropdowns/role-dropdown/RoleDropdown';
import {getFormattedValue} from '../timesheet-table-util/TimeRegUtil';
import {hasPermission} from '../../../../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../../Permissions';
import {FlexColumn, FlexRow, FlexItem} from '@forecast-it/design-system';

export enum PopupFocusItem {
	// Can be extended with e.g. hour inputs if needed in the future
	Notes,
	Hours = 1,
}

export type TimeRegistrationFocusType = {
	id: string | null;
	focusItem: PopupFocusItem | null;
};

function getTimeInputValue_timeRegInputConversion(timeInputMinutes: number): string | undefined {
	if (timeInputMinutes != null && !isNaN(timeInputMinutes)) {
		return `${timeInputMinutes}m`;
	}
	return undefined;
}

function getBillableTimeInputValue_timeRegInputConversion(
	billableTimeInputMinutes: number | null,
	timeInputValue: string | number | undefined
): string | number | undefined {
	if (billableTimeInputMinutes != null && !isNaN(billableTimeInputMinutes)) {
		return `${billableTimeInputMinutes}m`;
	}
	return timeInputValue;
}

interface AdvancedTimeRegistrationContentProps {
	id: string;
	timeInputMinutes: number;
	onTimeChange: (input: number, isEnterBlur?: boolean) => void;
	billableTimeInputMinutes: number | null;
	onBillableTimeChange: (input: number | null, isEnterBlur?: boolean) => void;
	notes: string;
	onNotesChange: (string) => void;
	billable: boolean;
	noteError: boolean;
	timeError?: string | null;
	remainingInfoMessage?: string | null;
	workingHours: number | undefined;
	timeRegistrationFocus: TimeRegistrationFocusType;
	role?: Role;
	onRoleChange: (role: Role) => void;
	firstTimeRegistration: boolean;
	canHaveRole: boolean;
	projectId?: string;
	taskId?: string;
	personId?: string;
	isCompanyTimesheet?: boolean;
	isTimeRegLocked?: boolean;
	tooltipMessage?: string;
}

function AdvancedTimeRegistrationContent({
	id,
	timeInputMinutes,
	onTimeChange,
	billableTimeInputMinutes,
	onBillableTimeChange,
	notes,
	onNotesChange,
	billable,
	noteError,
	timeError,
	remainingInfoMessage,
	workingHours,
	timeRegistrationFocus,
	role,
	onRoleChange,
	firstTimeRegistration,
	canHaveRole,
	projectId,
	taskId,
	personId,
	isCompanyTimesheet,
	isTimeRegLocked,
	tooltipMessage,
}: AdvancedTimeRegistrationContentProps): JSX.Element {
	const intl = useIntl();
	const billableMinutesUpdated = useRef(false);
	const initialNote = useRef(notes);

	const noteInputRef = useRef<HTMLTextAreaElement>(null);
	const hoursInputRef = useRef<HTMLInputElement>(null);

	const isNoteRequired = isTimeRegistrationNoteRequired();
	const billableSplitAllowed = isBillableSplitAllowed();

	const isEditOperation = !!id;

	const showRoles =
		isRolesAllowed() ||
		(isEditOperation && isCompanyTimesheet && hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_UPDATE_ALL));

	const hasTeamTimeAccess = Util.AuthorizeViewerAccess('team-time');

	const onHoursInputChange = (value, _, __, isEnterBlur: boolean) => {
		const formattedValue = getFormattedValue(value);
		onTimeChange(formattedValue, isEnterBlur);
		trackEvent('Advanced Time Registration', 'Time Input Changed', {
			from: timeInputMinutes,
			to: formattedValue,
		});
	};

	const onBillableHoursInputChange = (value, _, __, isEnterBlur) => {
		const formattedValue = getFormattedValue(value);
		onBillableTimeChange(formattedValue, isEnterBlur);
		trackEvent('Advanced Time Registration', 'Billable Time Input Changed', {
			from: billableTimeInputMinutes,
			to: formattedValue,
		});

		billableMinutesUpdated.current = true;
	};

	const handleNotesChange = e => {
		const note = e.target ? e.target.value : '';
		onNotesChange(note);
	};

	const onNotesBlur = () => {
		trackEvent('Advanced Time Registration', 'Note Changed', {from: initialNote, to: notes});
		initialNote.current = notes;
	};

	useUpdateEffect(() => {
		if (!billableMinutesUpdated.current) {
			onBillableTimeChange(null);
		}
	}, [timeInputMinutes]);

	useEffect(() => {
		if (timeRegistrationFocus.id === id) {
			switch (timeRegistrationFocus.focusItem) {
				case PopupFocusItem.Notes:
					noteInputRef.current?.focus();
					break;
				case PopupFocusItem.Hours:
					hoursInputRef.current?.focus();
					break;
			}
		}
	}, [timeRegistrationFocus]);

	// Controlled HoursInput works in hours
	const timeInputValue = getTimeInputValue_timeRegInputConversion(timeInputMinutes);
	// The value of the billable time input should be linked with the time input if it has not been changed yet
	const billableTimeInputValue = getBillableTimeInputValue_timeRegInputConversion(billableTimeInputMinutes, timeInputValue);

	return (
		<ContentWrapper data-cy={'time-entry-popup-content'}>
			<FlexRow gap="s">
				<FlexColumn flex="0">
					<Label>{intl.formatMessage({id: 'common.time_entry'})}</Label>
					<ForecastTooltip disabled={!isTimeRegLocked} content={tooltipMessage}>
						<HoursInput
							focusOnMount={firstTimeRegistration}
							innerRef={hoursInputRef}
							placeholder={'ex. 1h'}
							value={timeInputValue}
							mutation={onHoursInputChange}
							width={'90px'}
							granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
								convertIntoFloatHoursFormatWithGranularity(val, intl, minuteLimit, showAsterisk, workingHours)
							}
							onlyFormatGranularityOnBlur
							isTimeregInput
							disabled={isTimeRegLocked}
						/>
					</ForecastTooltip>
				</FlexColumn>

				{billable && billableSplitAllowed && (
					<FlexColumn flex="0">
						<Label>{intl.formatMessage({id: 'common.billable_time'})}</Label>
						<HoursInput
							placeholder={'ex. 1h'}
							value={billableTimeInputValue}
							mutation={onBillableHoursInputChange}
							width={'90px'}
							granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
								convertIntoFloatHoursFormatWithGranularity(val, intl, minuteLimit, showAsterisk, workingHours)
							}
							onlyFormatGranularityOnBlur
							isTimeregInput
						/>
					</FlexColumn>
				)}

				{canHaveRole && (
					<FlexItem flex="1">
						<ForecastTooltip
							disabled={showRoles}
							content={
								hasTeamTimeAccess
									? 'The role used on the time registration can be changed from the Timesheet Entries Page or Timesheet Approval Page'
									: 'Role selection on time entries can be enabled by your admin in Forecast settings.'
							}
						>
							<FlexColumn>
								<RoleDropdownRelayWrapper
									role={role}
									onSelect={onRoleChange}
									isDisabled={!showRoles}
									projectId={projectId}
									taskId={taskId}
									personId={personId}
									showLabel={true}
									labelFormat={RoleDropdownLabelFormat.NO_MARGIN}
								/>
							</FlexColumn>
						</ForecastTooltip>
					</FlexItem>
				)}
			</FlexRow>
			<RemainingTimeAndError timeError={timeError} remainingInfoMessage={remainingInfoMessage} marginBottom={'0'} />
			<FlexColumn>
				<Label>
					<LabelWithTooltip label={'Note'} isRequired={isNoteRequired} isTimePage={true}>
						<NoteRequiredInfoIcon />
					</LabelWithTooltip>
				</Label>
				<Notes
					value={notes}
					ref={noteInputRef}
					maxLength={999}
					rows={timeError ? 2 : 3}
					cols={40}
					placeholder={'Enter notes'}
					onChange={handleNotesChange}
					onBlur={onNotesBlur}
				/>
				{noteError ? <NoteRequiredError /> : null}
			</FlexColumn>
		</ContentWrapper>
	);
}

export default AdvancedTimeRegistrationContent;
